import { clamp, desktopSize } from '@/lib/dom';
import {
  CASCADE_ORIGIN,
  CASCADE_STEP,
  CASCADE_WRAP,
  MIN_VISIBLE,
  TASKBAR_H,
  TITLE_H,
} from './constants';
import type { Rect } from './types';

export interface Viewport {
  w: number;
  h: number;
}

/** Usable desktop area: viewport minus taskbar. */
export function viewport(): Viewport {
  return desktopSize(TASKBAR_H);
}

/**
 * Keep a window reachable: the title bar may never go above the top of the
 * screen, and at least `MIN_VISIBLE` px must remain grabbable horizontally.
 */
export function clampPosition(x: number, y: number, w: number, vp: Viewport): { x: number; y: number } {
  return {
    x: clamp(x, -(w - MIN_VISIBLE), Math.max(0, vp.w - MIN_VISIBLE)),
    y: clamp(y, 0, Math.max(0, vp.h - TITLE_H)),
  };
}

export function clampRect(rect: Rect, vp: Viewport = viewport()): Rect {
  const { x, y } = clampPosition(rect.x, rect.y, rect.w, vp);
  return { ...rect, x, y };
}

/** Shrink a requested size so it always fits on small viewports. */
export function fitSize(size: { w: number; h: number }, vp: Viewport = viewport()): { w: number; h: number } {
  return {
    w: Math.min(size.w, Math.max(200, vp.w - 16)),
    h: Math.min(size.h, Math.max(140, vp.h - 16)),
  };
}

/** Stepped diagonal placement for each successive window. */
export function cascade(index: number, size: { w: number; h: number }, vp: Viewport = viewport()): Rect {
  const step = index % CASCADE_WRAP;
  const x = CASCADE_ORIGIN.x + step * CASCADE_STEP;
  const y = CASCADE_ORIGIN.y + step * CASCADE_STEP;
  const fitted = fitSize(size, vp);
  // If the cascade would push the window mostly off-screen, wrap back.
  const overflowX = x + fitted.w > vp.w;
  const overflowY = y + fitted.h > vp.h;
  const finalX = overflowX ? Math.max(8, Math.min(x, vp.w - fitted.w - 8)) : x;
  const finalY = overflowY ? Math.max(8, Math.min(y, vp.h - fitted.h - 8)) : y;
  return clampRect({ x: finalX, y: finalY, ...fitted }, vp);
}

/** Centre a window on the desktop, or on its parent window when it's a dialog. */
export function centre(
  size: { w: number; h: number },
  vp: Viewport = viewport(),
  parent?: Rect,
): Rect {
  const fitted = fitSize(size, vp);
  const base = parent
    ? { x: parent.x + (parent.w - fitted.w) / 2, y: parent.y + (parent.h - fitted.h) / 2 }
    : { x: (vp.w - fitted.w) / 2, y: (vp.h - fitted.h) / 2 };
  return clampRect({ x: Math.round(base.x), y: Math.round(base.y), ...fitted }, vp);
}

export function maximizedRect(vp: Viewport = viewport()): Rect {
  return { x: 0, y: 0, w: vp.w, h: vp.h };
}

/**
 * Apply a resize in one direction, keeping the opposite edge pinned and
 * respecting the minimum size.
 */
export function resizeRect(
  start: Rect,
  dir: string,
  dx: number,
  dy: number,
  minSize: { w: number; h: number },
): Rect {
  let { x, y, w, h } = start;

  if (dir.includes('e')) {
    w = Math.max(minSize.w, start.w + dx);
  }
  if (dir.includes('s')) {
    h = Math.max(minSize.h, start.h + dy);
  }
  if (dir.includes('w')) {
    const proposed = Math.max(minSize.w, start.w - dx);
    x = start.x + (start.w - proposed);
    w = proposed;
  }
  if (dir.includes('n')) {
    const proposed = Math.max(minSize.h, start.h - dy);
    y = start.y + (start.h - proposed);
    h = proposed;
  }

  // Never let a north-drag push the title bar above the desktop.
  if (y < 0) {
    h += y;
    y = 0;
    h = Math.max(minSize.h, h);
  }

  return { x, y, w, h };
}
